import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import {
  skipToken,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import {
  Check,
  Lightbulb,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  Plus,
  RotateCcw,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { MailComposer } from '../../mail/-components/composer/mail-composer';
import { useComposerState } from './composer-state';
import type { ComposerReplyView, ComposerView } from './composer-state';
import type { ReactNode } from 'react';
import type { Id } from '@minty/shared/ids';
import type { RouterOutputs } from '~/lib/trpc';
import { useCan } from '~/hooks/use-can';
import {
  ChannelPill,
  ComposerHeader,
  MessageComposer,
} from '~/components/message-composer';
import { callErrorMessage } from '~/components/call-error-message';
import { SmsComposer } from '~/components/sms-composer';
import { Skeleton } from '~/components/ui/skeleton';
import { Button } from '~/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import { Textarea } from '~/components/ui/textarea';
import { appErrorCode } from '~/lib/app-error';
import { notify } from '~/lib/toast';
import { getPostHog } from '~/lib/posthog';
import { useTRPC } from '~/lib/trpc';
import { cn } from '~/lib/utils';

/** The bounds the server holds a task to, so the form refuses first. */
const TASK_TITLE_MAX_LENGTH = 120;
const TASK_SUMMARY_MAX_LENGTH = 400;

export function OpenTaskPill({ contactId }: { contactId: Id<'contacts'> }) {
  const trpc = useTRPC();
  const { data: task } = useSuspenseQuery(
    trpc.tasks.management.getOpenForContact.queryOptions({ contactId }),
  );

  if (!task) {
    return <ManualTaskCreator contactId={contactId} />;
  }

  return <TaskPill taskId={task.id} title={task.title} />;
}

/**
 * Put the last finished task back.
 *
 * Attached to the contact it is about, for as long as the task is the last one
 * they had — rather than to a toast, which offers the undo to whoever happens
 * to be looking and to nobody afterwards.
 *
 * Only asked for where there is no open task: `reopen` refuses while one
 * exists, so on any other contact the answer could not be acted on.
 */
function RestoreTaskButtonContent({
  contactId,
}: {
  contactId: Id<'contacts'>;
}) {
  const { t } = useTranslation('tasks');
  const trpc = useTRPC();
  const { data: task } = useQuery(
    trpc.tasks.management.getLastCompletedForContact.queryOptions({
      contactId,
    }),
  );
  const { mutateAsync: reopenTask } = useMutation(
    trpc.tasks.management.reopen.mutationOptions(),
  );
  const [isSaving, setIsSaving] = useState(false);

  if (!task) {
    return null;
  }

  const handleRestore = async () => {
    setIsSaving(true);

    try {
      await reopenTask({ taskId: task.id });
    } catch (error) {
      /** Somebody opened one while this was on screen. The refusal is the
       *  point — two open tasks on one contact is what it exists to stop. */
      notify.error(
        appErrorCode(error) === 'task_conflict'
          ? t('A newer open task already exists for this contact.')
          : t('Unable to reopen task.'),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      tone="info"
      size="xs"
      disabled={isSaving}
      onClick={() => void handleRestore()}
      title={t('Restore “{{title}}”', { title: task.title })}
    >
      <RotateCcw className="size-3.5" strokeWidth={2.5} />
      {t('Restore task')}
    </Button>
  );
}

function ManualTaskCreatorContent({
  contactId,
}: {
  contactId: Id<'contacts'>;
}) {
  const { t } = useTranslation('tasks');
  const trpc = useTRPC();
  const { mutateAsync: createTask } = useMutation(
    trpc.tasks.management.create.mutationOptions(),
  );
  const [isOpen, setIsOpen] = useState(false);
  const schema = useMemo(
    () =>
      z.object({
        title: z
          .string()
          .trim()
          .min(1, t('Task title is required.'))
          .max(TASK_TITLE_MAX_LENGTH, t('Task title is too long.')),
        summary: z
          .string()
          .trim()
          .min(1, t('Task summary is required.'))
          .max(TASK_SUMMARY_MAX_LENGTH, t('Task summary is too long.')),
      }),
    [t],
  );
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { title: '', summary: '' },
  });

  if (!isOpen) {
    return (
      <div className="mx-10 flex shrink-0 items-center justify-end gap-1 rounded-t-2xl border-x border-t border-dashed border-info/30 bg-info/5 px-3 py-1.5">
        <RestoreTaskButton contactId={contactId} />
        <Button
          type="button"
          variant="ghost"
          tone="info"
          size="xs"
          onClick={() => setIsOpen(true)}
        >
          <Plus className="size-3.5" strokeWidth={2.5} />
          {t('Create task')}
        </Button>
      </div>
    );
  }

  const handleSubmit = async (values: z.infer<typeof schema>) => {
    try {
      await createTask({ contactId, ...values });
      form.reset();
      setIsOpen(false);
      notify.success(t('Task created.'));
    } catch (error) {
      notify.error(
        appErrorCode(error) === 'task_exists'
          ? t('An open task already exists for this contact.')
          : t('Unable to create task.'),
      );
    }
  };
  let submitIcon = <Plus />;

  if (form.formState.isSubmitting) {
    submitIcon = <Loader2 className="animate-spin" />;
  }

  return (
    <div className="mx-10 shrink-0 rounded-t-2xl border-x border-t border-info/30 bg-info/5 p-3">
      <Form.Root form={form} onSubmit={handleSubmit} className="space-y-2">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem className="gap-1">
              <FormControl>
                <Input
                  {...field}
                  autoFocus
                  maxLength={TASK_TITLE_MAX_LENGTH}
                  disabled={form.formState.isSubmitting}
                  placeholder={t('Task title')}
                  aria-label={t('Task title')}
                  className="h-8 bg-background text-xs"
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="summary"
          render={({ field }) => (
            <FormItem className="gap-1">
              <FormControl>
                <Textarea
                  {...field}
                  maxLength={TASK_SUMMARY_MAX_LENGTH}
                  disabled={form.formState.isSubmitting}
                  placeholder={t('Task summary')}
                  aria-label={t('Task summary')}
                  className="min-h-16 resize-none bg-background text-xs"
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="xs"
            disabled={form.formState.isSubmitting}
            onClick={() => {
              form.reset();
              setIsOpen(false);
            }}
          >
            <X />
            {t('Cancel')}
          </Button>
          <Button
            type="submit"
            size="xs"
            disabled={form.formState.isSubmitting}
          >
            {submitIcon}
            {t('Create task')}
          </Button>
        </div>
      </Form.Root>
    </div>
  );
}

// The task pill sits at the bottom of the activities feed, directly above the
// composer card, so the two read as one tab and its card.
export function TaskPill({
  taskId,
  title,
}: {
  taskId: Id<'tasks'>;
  title: string;
}) {
  const canWork = useCan('reception');
  const { t } = useTranslation('tasks');
  const trpc = useTRPC();
  const { mutateAsync: completeTask } = useMutation(
    trpc.tasks.management.complete.mutationOptions(),
  );
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  // No local pending flag: the pill unmounts once getOpenForContact turns
  // null, and a repeated complete is a no-op.
  const handleComplete = async () => {
    try {
      await completeTask({ taskId });
      notify.success(t('Task completed.'));

      /**
       * Completing takes the contact out of the task inbox, so the pane is
       * left showing somebody the list beside it no longer has — go back to
       * it. The contact inbox lists people regardless of their tasks, so
       * there the row is still there and there is nowhere to go.
       */
      if (pathname.startsWith('/tasks')) {
        await navigate({ to: '/tasks', replace: true });
      }
    } catch {
      notify.error(t('Unable to complete task.'));
    }
  };

  return (
    <div className="mx-10 flex shrink-0 items-center justify-between rounded-t-2xl border-x border-t border-info/30 bg-info/5 px-3 py-1.5 text-info">
      <div className="flex min-w-0 items-center gap-2">
        <Lightbulb className="size-4 shrink-0" strokeWidth={2.5} />
        <p className="min-w-0 truncate text-xs font-medium" title={title}>
          {title}
        </p>
      </div>
      <Button
        type="button"
        variant="outline"
        tone="info"
        size="icon-xs"
        shape="pill"
        disabled={!canWork}
        onClick={() => void handleComplete()}
        aria-label={t('Mark task as complete')}
      >
        <Check className="size-4" strokeWidth={2.5} />
      </Button>
    </div>
  );
}

type OpenTask = RouterOutputs['tasks']['management']['getOpenForContact'];
type SuggestedAction = NonNullable<NonNullable<OpenTask>['suggestedAction']>;
type EmailProvider = RouterOutputs['config']['queries']['getEmailProviderKind'];

export type TaskMessageComposerProps = {
  contactId: Id<'contacts'>;
  email?: string;
  phoneNumber?: string;
  task: OpenTask;
  emailProvider: EmailProvider;
  lastReply: ComposerReplyView | null;
  isLastReplyTargetPending: boolean;
};

function TaskMessageComposerContent({
  contactId,
  email,
  phoneNumber,
  task,
  emailProvider,
  lastReply,
  isLastReplyTargetPending,
}: TaskMessageComposerProps) {
  const { t } = useTranslation('mailbox');
  const { t: tTasks } = useTranslation('tasks');
  const { state, setState } = useComposerState();
  const trpc = useTRPC();
  const { mutateAsync: consumeSuggestedAction } = useMutation(
    trpc.tasks.management.consumeSuggestedAction.mutationOptions(),
  );
  const storedSuggestion = task?.suggestedAction;
  const isReplyTargetPending = isLastReplyTargetPending;
  // A reply always targets the contact's newest inbound email: someone
  // clicking reply now wants the newest, whatever the suggestion was about.
  /** Asked only while a forward is open. */
  const forwardEmailId =
    state.view === 'email' && state.mode === 'forward'
      ? state.target.emailId
      : null;
  const { data: forwardSource } = useQuery(
    trpc.activities.feed.getEmailForwardSource.queryOptions(
      forwardEmailId ? { emailId: forwardEmailId } : skipToken,
    ),
  );
  const activeSuggestion = toActionableSuggestion(storedSuggestion, {
    email,
    phoneNumber,
    isReplyTargetPending,
    hasReplyTarget: lastReply !== null,
  });
  async function consumeSuggestion() {
    if (!task?.suggestedAction) {
      return;
    }

    try {
      await consumeSuggestedAction({ taskId: task.id });
    } catch (error) {
      getPostHog()?.captureException(error);
    }
  }

  const { mutateAsync: startCall } = useMutation(
    trpc.calls.outbound.startOutboundCall.mutationOptions(),
  );
  const handleCall = async () => {
    try {
      await startCall({ contactId });

      if (activeSuggestion === 'call') {
        await consumeSuggestion();
      }
    } catch (error) {
      getPostHog()?.captureException(error);
      notify.error(callErrorMessage(error, tTasks));
    }
  };

  const closeComposer = () => setState({ view: 'collapsed' });
  const openEmail = () => {
    if (activeSuggestion === 'reply_email' && lastReply) {
      setState(lastReply);
    } else {
      setState(newEmailView(emailProvider));
    }
  };
  const openSms = () => setState({ view: 'sms' });
  const makeHandleSent = (usedSuggestion: boolean) => () => {
    if (usedSuggestion) {
      void consumeSuggestion();
    }
    closeComposer();
  };

  const collapsedStrip = (
    <ComposerCard>
      <CollapsedComposerStrip
        email={email}
        phoneNumber={phoneNumber}
        suggestedAction={activeSuggestion}
        onOpenEmail={openEmail}
        onOpenSms={openSms}
        onCall={handleCall}
      />
    </ComposerCard>
  );

  if (state.view === 'collapsed') {
    return collapsedStrip;
  }

  const headerChannel = state.view === 'sms' ? 'sms' : 'email';
  /** The one line under it is the only thing that differs between the views. */
  const makeHeader = (context?: ReactNode) => (
    <TaskComposerHeader
      channel={headerChannel}
      context={context}
      emailDisabled={!email}
      smsDisabled={!phoneNumber}
      suggestedAction={activeSuggestion}
      onCall={handleCall}
      onOpenEmail={openEmail}
      onOpenSms={openSms}
      onClose={closeComposer}
    />
  );
  const header = makeHeader();

  if (state.view === 'sms') {
    // Not reachable through the UI (SMS buttons are disabled without a phone
    // number); fall back to the collapsed strip rather than a broken composer.
    if (!phoneNumber) {
      return collapsedStrip;
    }

    return (
      <ComposerCard>
        <SmsComposer
          key={`${contactId}:sms`}
          contactId={contactId}
          phoneNumber={phoneNumber}
          header={header}
          onSent={makeHandleSent(activeSuggestion === 'sms')}
        />
      </ComposerCard>
    );
  }

  if (state.view === 'mail') {
    if (state.mode === 'new') {
      if (!email) {
        return collapsedStrip;
      }

      return (
        <ComposerCard>
          <MailComposer
            key={`${contactId}:mail:new`}
            mode="new"
            lockTo
            to={email}
            header={header}
            onSent={makeHandleSent(activeSuggestion === 'new_email')}
          />
        </ComposerCard>
      );
    }

    const isSuggestedReply =
      activeSuggestion === 'reply_email' &&
      state.mode === 'reply' &&
      lastReply?.target.emailId === state.target.emailId;
    const subject = state.target.subject.trim();
    const composerHeader = makeHeader(
      <p
        title={state.target.fromEmail}
        className="min-w-0 truncate text-xs font-medium text-muted-foreground"
      >
        {state.mode === 'forward'
          ? tTasks('Forwarding: {{subject}}', { subject })
          : tTasks('Replying to: {{target}}', {
              target: subject || state.target.fromEmail,
            })}
      </p>,
    );

    return (
      <ComposerCard>
        <MailComposer
          key={`${contactId}:${state.mode}:${state.target.emailId}`}
          emailId={state.target.emailId}
          mode={state.mode}
          header={composerHeader}
          onSent={makeHandleSent(isSuggestedReply)}
        />
      </ComposerCard>
    );
  }

  // Only email states remain: forward, then reply, then new-email.
  if (state.mode === 'forward') {
    // The message was filed to spam or trash while the menu was open. The send
    // would refuse it, so the composer never opens on it.
    if (forwardSource === null) {
      return collapsedStrip;
    }

    const composerHeader = makeHeader(
      <p className="min-w-0 truncate text-xs font-medium text-muted-foreground">
        {tTasks('Forwarding: {{subject}}', {
          subject: forwardSource?.subject.trim() ?? '',
        })}
      </p>,
    );

    /* `keptForwardAttachmentIds` is seeded into the form at mount and never
       re-read, so a composer that mounts before the source arrives would
       silently forward none of its attachments. */
    if (forwardSource === undefined) {
      return (
        <ComposerCard>
          <div className="flex flex-col gap-3 px-4 pb-4 pt-3">
            {composerHeader}
            <Skeleton className="h-48 w-full" />
          </div>
        </ComposerCard>
      );
    }

    return (
      <ComposerCard>
        <MessageComposer
          key={`${contactId}:forward:${forwardSource.emailId}`}
          contactId={contactId}
          mode="forward"
          originalEmail={{ emailId: forwardSource.emailId }}
          subject={forwardSource.subject}
          forwardAttachments={forwardSource.attachments}
          header={composerHeader}
          onSent={makeHandleSent(false)}
        />
      </ComposerCard>
    );
  }

  if (state.mode === 'reply') {
    const isSuggestedReply =
      activeSuggestion === 'reply_email' &&
      lastReply?.target.emailId === state.target.emailId;
    const replyTargetLabel = state.target.reply.to.join(', ');
    const composerHeader = makeHeader(
      <p
        title={replyTargetLabel}
        aria-label={tTasks('Replying to: {{target}}', {
          target: replyTargetLabel,
        })}
        className="min-w-0 truncate text-xs font-medium text-muted-foreground"
      >
        {tTasks('Replying to: {{target}}', {
          target: state.target.subject.trim() || replyTargetLabel,
        })}
      </p>,
    );

    return (
      <ComposerCard>
        <MessageComposer
          key={`${contactId}:reply:${state.target.emailId}`}
          contactId={contactId}
          mode="reply"
          originalEmail={state.target}
          subject={state.target.subject}
          header={composerHeader}
          submitLabel={t('Send')}
          onSent={makeHandleSent(isSuggestedReply)}
        />
      </ComposerCard>
    );
  }

  // Same guard as SMS above: the new-email buttons are disabled without an
  // email address.
  if (!email) {
    return collapsedStrip;
  }

  const isSuggestedNewEmail = activeSuggestion === 'new_email';

  return (
    <ComposerCard>
      <MessageComposer
        key={`${contactId}:new`}
        contactId={contactId}
        mode="new"
        lockTo
        to={email}
        header={header}
        submitLabel={t('Send')}
        onSent={makeHandleSent(isSuggestedNewEmail)}
      />
    </ComposerCard>
  );
}

function newEmailView(emailProvider: EmailProvider): ComposerView {
  return emailProvider === 'emailEngine'
    ? { view: 'mail', mode: 'new' }
    : { view: 'email', mode: 'new' };
}

// A suggestion is only actionable while its channel has a destination.
function toActionableSuggestion(
  suggestion: SuggestedAction | null | undefined,
  destinations: {
    email?: string;
    phoneNumber?: string;
    isReplyTargetPending: boolean;
    hasReplyTarget: boolean;
  },
): SuggestedAction | undefined {
  if (!suggestion) {
    return undefined;
  }

  switch (suggestion) {
    case 'call':
    case 'sms':
      return destinations.phoneNumber ? suggestion : undefined;
    case 'new_email':
      return destinations.email ? suggestion : undefined;
    case 'reply_email':
      // While the reply-target query is pending the suggestion stays visible.
      return destinations.isReplyTargetPending || destinations.hasReplyTarget
        ? suggestion
        : undefined;
  }
}

/**
 * The composer opens once when its contact/task scope becomes ready. Keeping
 * this as a pure derivation lets the keyed provider establish the right first
 * render without synchronizing interface state from an Effect.
 */
export function getInitialTaskComposerView({
  task,
  email,
  phoneNumber,
  emailProvider,
  lastReply,
}: {
  task: OpenTask;
  email?: string;
  phoneNumber?: string;
  emailProvider: EmailProvider;
  lastReply: ComposerReplyView | null;
}): ComposerView {
  if (!task) {
    return { view: 'collapsed' };
  }

  const activeSuggestion = toActionableSuggestion(task.suggestedAction, {
    email,
    phoneNumber,
    isReplyTargetPending: false,
    hasReplyTarget: lastReply !== null,
  });

  if (activeSuggestion === 'sms' && phoneNumber) {
    return { view: 'sms' };
  }

  if (activeSuggestion === 'reply_email' && lastReply) {
    return lastReply;
  }

  if (email) {
    return newEmailView(emailProvider);
  }

  if (phoneNumber) {
    return { view: 'sms' };
  }

  return { view: 'collapsed' };
}

function ComposerCard({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col overflow-hidden px-4 pb-4">
      <div className="flex flex-col overflow-hidden rounded-2xl border border-primary bg-card">
        {children}
      </div>
    </div>
  );
}

const SUGGESTED_PILL_CLASS =
  'border-info bg-info/10 text-info ring-2 ring-info/20 hover:bg-info/15 hover:text-info';

function CollapsedComposerStrip({
  email,
  phoneNumber,
  suggestedAction,
  onOpenEmail,
  onOpenSms,
  onCall,
}: {
  email?: string;
  phoneNumber?: string;
  suggestedAction?: SuggestedAction;
  onOpenEmail: () => void;
  onOpenSms: () => void;
  onCall: () => void;
}) {
  const { t } = useTranslation('tasks');
  const isEmailSuggested =
    suggestedAction === 'new_email' || suggestedAction === 'reply_email';

  return (
    <div className="flex items-center justify-between gap-2 px-4 py-4">
      <div className="flex items-center gap-1">
        <ChannelPill
          icon={Mail}
          label={t('Email')}
          disabled={!email}
          onClick={onOpenEmail}
          className={cn(isEmailSuggested && SUGGESTED_PILL_CLASS)}
        />
        <ChannelPill
          icon={MessageSquare}
          label={t('SMS')}
          disabled={!phoneNumber}
          onClick={onOpenSms}
          className={cn(suggestedAction === 'sms' && SUGGESTED_PILL_CLASS)}
        />
      </div>
      <div className="flex min-w-0 items-center gap-2">
        <CallButton
          suggested={suggestedAction === 'call'}
          disabled={!phoneNumber}
          onCall={onCall}
        />
      </div>
    </div>
  );
}

/** The channel it is on, and nothing about what it is doing there: a reply is
 *  opened as one from the message it answers, the same as in the mailbox. */
function TaskComposerHeader({
  channel,
  context,
  emailDisabled,
  smsDisabled,
  suggestedAction,
  onCall,
  onOpenEmail,
  onOpenSms,
  onClose,
}: {
  channel: 'email' | 'sms';
  context?: ReactNode;
  emailDisabled: boolean;
  smsDisabled: boolean;
  suggestedAction?: SuggestedAction;
  onCall: () => void;
  onOpenEmail: () => void;
  onOpenSms: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('tasks');

  return (
    <ComposerHeader
      context={context}
      onClose={onClose}
      actions={
        <CallButton suggested={suggestedAction === 'call'} onCall={onCall} />
      }
    >
      <ChannelPill
        active={channel === 'email'}
        icon={Mail}
        label={t('Email')}
        disabled={emailDisabled}
        onClick={onOpenEmail}
      />
      <ChannelPill
        active={channel === 'sms'}
        icon={MessageSquare}
        label={t('SMS')}
        disabled={smsDisabled}
        onClick={onOpenSms}
      />
    </ComposerHeader>
  );
}

function CallButton({
  suggested,
  disabled,
  onCall,
}: {
  suggested: boolean;
  disabled?: boolean;
  onCall: () => void;
}) {
  const { t } = useTranslation('tasks');

  return (
    <Button
      type="button"
      variant="outline"
      tone="success"
      shape="pill"
      disabled={disabled}
      onClick={onCall}
      className={cn(suggested && SUGGESTED_PILL_CLASS)}
    >
      <Phone className="size-4" strokeWidth={2.5} />
      {t('Call')}
    </Button>
  );
}

function RestoreTaskButton(
  props: Parameters<typeof RestoreTaskButtonContent>[0],
) {
  const allowed = useCan('reception');
  return allowed ? <RestoreTaskButtonContent {...props} /> : null;
}

function ManualTaskCreator(
  props: Parameters<typeof ManualTaskCreatorContent>[0],
) {
  const allowed = useCan('reception');
  return allowed ? <ManualTaskCreatorContent {...props} /> : null;
}

export function TaskMessageComposer(
  props: Parameters<typeof TaskMessageComposerContent>[0],
) {
  const allowed = useCan('reception');
  return allowed ? <TaskMessageComposerContent {...props} /> : null;
}
